import React, { useEffect, useMemo, useState, useContext, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useSpacing from '../../core/hooks/useSpacing';
import useBaseStyle from '../../core/hooks/useBaseStyle';
import Item from "./Item";
import EditableElement from "../../editor/components/EditableElement";
import { DndContext, closestCenter, PointerSensor, useSensors, useSensor } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy } from "@dnd-kit/sortable";
import { DragHandleProvider } from "../../editor/context/DragHandleContext";
import { makeSortableId, reorderArray } from "../../editor/utils/reorderCache";
import { reorderEntity, reorderButtonCollection } from "../../editor/utils/reorderMove";
import { apiFetch } from "../../core/utils/api";
import AddButtonWithIndicator from "../../editor/components/AddButtonWithIndicator";
import AddButtonOverlay from "../../editor/add-buttons/AddButtonOverlay";
import { useAddElement } from "../../editor/context/AddElementContext";
import { EditContext } from "../../editor/context/EditContext";
import PreviewContext from "../../editor/context/PreviewContext";
import { resolveButtonCollectionAlignment } from "../../core/utils/characterStyleUtils";

const SortableWrapper = React.memo(function SortableWrapper({ id, data, gridStyle, children }) {
  // dnd-kit treats `data` as an input to its internal registration effects.
  // Passing a new object each render can cause re-registration churn and, in some cases,
  // "Maximum update depth exceeded" loops. Stabilize by memoizing by value.
  const stableData = useMemo(() => data, [JSON.stringify(data ?? null)]);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: stableData,
  });
  const style = {
    // IMPORTANT: grid positioning MUST be applied to the direct child of the grid container.
    // Our sortable wrapper is the direct child, so apply gridRow/gridColumn here (not inside).
    ...(gridStyle || {}),
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.18 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <DragHandleProvider value={{ dragListeners: listeners, dragAnywhereEnabled: true }}>{children}</DragHandleProvider>
    </div>
  );
});
SortableWrapper.displayName = "SortableWrapper";

/**
 * Detects if button data is fully serialized (preview mode) or minimal ref (lazy loading).
 * 
 * See Hero.js for full documentation. This helper enables button collections to work
 * with both template previews (full data) and normal website viewing (lazy loading).
 * 
 * @param {Object} child - Button data
 * @returns {boolean} True if button has full data, false if minimal ref
 */
const hasFullData = (child) => {
  if (!child) return false;
  const minimalFields = ['id', 'type', 'order'];
  const allFields = Object.keys(child);
  return allFields.some(field => !minimalFields.includes(field));
};

const computeColumnsStyle = (item, cols, preview) => {
  // We'll use CSS grid with inline columns so we can control track type
  const colGap = item.gap_horizontal ?? 4;
  const rowGap = item.gap_vertical ?? 8;
  const base = {
    display: "grid",
    // Set both specific and shorthand gap to maximize cross-browser behavior
    columnGap: `${colGap}px`,
    rowGap: `${rowGap}px`,
    gap: `${rowGap}px ${colGap}px`,
  };

  // ``alignment`` is now a ``ResponsiveButtonCollectionAlignment`` wrapper
  // (object with per-breakpoint keys); resolve it to the matching string for
  // the current viewport. Strings still pass through, which keeps legacy
  // payloads / hand-rolled test fixtures rendering unchanged.
  const resolvedAlign = resolveButtonCollectionAlignment(item.alignment, preview);
  const align = (resolvedAlign || "center").toLowerCase();
  // Align items within their grid areas
  if (align === "full") {
    base.justifyItems = "stretch";
  } else if (align === "start") {
    base.justifyItems = "start";
  } else if (align === "center") {
    base.justifyItems = "center";
  } else if (align === "end") {
    base.justifyItems = "end";
  }
  // Center the whole grid (track group) inside its container
  if (align === "full") {
    base.justifyContent = "stretch";
  } else if (align === "start") {
    base.justifyContent = "start";
  } else if (align === "center") {
    base.justifyContent = "center";
  } else if (align === "end") {
    base.justifyContent = "end";
  }

  // Track type: for 'full' we want 1fr tracks (each button stretches to
  // share the row equally); otherwise content-sized tracks so the gap —
  // not the track width — controls spacing between buttons.
  //
  // ``minmax(0, max-content)`` instead of bare ``max-content`` is what
  // lets the grid honour a constrained container. With bare
  // ``max-content`` the grid pins each track to its widest button's
  // intrinsic width and overflows the container when the sum doesn't
  // fit (the user-reported bug: "VIEW PROJECTS, ABOUT ME, CO…" running
  // off the right edge of a Split / Gallery hero column). The
  // ``minmax(0, …)`` lower bound tells the grid algorithm "you may
  // shrink tracks below max-content if forced to" — buttons then
  // share the available width instead of pushing past it. When there's
  // enough room the upper ``max-content`` bound still wins so the
  // visual remains identical for the common case.
  const track = align === "full" ? "1fr" : "minmax(0, max-content)";
  base.gridTemplateColumns = `repeat(${Math.max(1, cols || 1)}, ${track})`;
  // Pair the shrinkable tracks with ``min-width: 0`` on the grid
  // container itself — without this the grid's own ``min-width: auto``
  // (intrinsic size of its widest tracks) would re-pin it to the
  // overflowing width and defeat the ``minmax`` change.
  base.minWidth = 0;

  return { base };
};

const ButtonCollection = ({ item, itemRef, containerWidth: providedContainerWidth }) => {
  const hasRef = !!itemRef;
  const needsFetch = !item && hasRef;
  const id = hasRef ? itemRef.id : item?.id;
  const hasUsableItem = !!item && Array.isArray(item.buttons);
  
  // Edit mode detection and add element context
  const editContext = useContext(EditContext);
  const { openAddModal } = useAddElement();
  const isEditMode = editContext?.selectedElement !== undefined;
  const queryClient = useQueryClient();
  const suppressClickUntilRef = useRef(0);
  const suppressExternalSyncUntilRef = useRef(0);
  
  // Width source for responsive layout:
  // - Prefer measuring the *actual rendered* collection width (container-query behavior). This fixes
  //   cases like sidebars/columns where `window.innerWidth` / preview width is larger than the
  //   available space for this collection.
  // - Fall back to provided `containerWidth` (from parents like Hero) and then PreviewContext.
  const preview = useContext(PreviewContext);
  const previewWidth = preview && typeof preview.contentWidth === "number" 
    ? preview.contentWidth 
    : null;
  const shouldDisableFetch = Boolean(preview?.isPresetStylePreview);

  const { data: fetched, isLoading, error, isError } = useQuery({
    queryKey: ["item", "button_collection", id],
    queryFn: async () => {
      try {
        const res = await apiFetch(`/api/items/button_collection/${id}/`);
        if (!res.ok) {
          const errorText = await res.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { error: errorText || `HTTP ${res.status}` };
          }
          const error = new Error(errorData.error || `Failed to fetch button_collection: ${res.status}`);
          error.status = res.status;
          throw error;
        }
        return res.json();
      } catch (err) {
        // Detect network errors (no response from server, connection refused, etc.)
        // Common network error patterns:
        // - TypeError from fetch API when network is unavailable
        // - NetworkError when request fails completely
        // - "Failed to fetch" message
        // - "Network request failed" message
        const isNetworkErrorType = 
          err instanceof TypeError || 
          err instanceof DOMException ||
          (err.constructor && err.constructor.name === 'NetworkError');
        
        const hasNetworkErrorMessage = 
          err.message && (
            err.message.includes('fetch') ||
            err.message.includes('network') ||
            err.message.includes('Failed to fetch') ||
            err.message.includes('Network request failed') ||
            err.message.includes('NetworkError') ||
            err.message.includes('connection') ||
            err.message.includes('ERR_INTERNET_DISCONNECTED') ||
            err.message.includes('ERR_NETWORK_CHANGED')
          );
        
        if (isNetworkErrorType || hasNetworkErrorMessage) {
          const networkError = new Error('Network connection lost');
          networkError.isNetworkError = true;
          networkError.originalError = err;
          throw networkError;
        }
        // Re-throw other errors (HTTP errors, etc.)
        throw err;
      }
    },
    // Keep this query always enabled when id exists.
    // Even with initialData, this allows invalidation/refetch after reorder so UI converges to server truth.
    enabled: !!id && !shouldDisableFetch,
    staleTime: 30 * 1000,
    cacheTime: 5 * 60 * 1000, // Keep cached data for 5 minutes
    retry: (failureCount, error) => {
      // Retry network errors with exponential backoff (up to 3 times)
      if (error?.isNetworkError) {
        return failureCount < 3;
      }
      // Don't retry HTTP errors (4xx, 5xx) - these are likely permanent
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      // Retry other errors up to 2 times
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s
    keepPreviousData: true,
    refetchOnWindowFocus: false, // Don't refetch on window focus to avoid network spam
    placeholderData: (previousData) => previousData, // Keep previous data while refetching
    initialData: hasUsableItem ? item : undefined,
  });

  // Call hooks unconditionally to satisfy rules-of-hooks
  // Prefer fully provided item over fetched; fallback to minimal ref
  // For network errors, React Query's placeholderData will preserve previous data
  // We prioritize: fetched data > item prop > itemRef > null
  const maybeEffective = fetched || item || itemRef || null;
  const { combinedClasses } = useSpacing(maybeEffective);
  const { styleClasses, inlineStyles } = useBaseStyle(maybeEffective);

  // Always provide a stable object so hooks below are never conditional.
  // This ensures we always have something to work with even during network errors
  const effective = maybeEffective || {};
  
  // Determine if we have any usable data (fetched, item prop, or itemRef)
  // This helps us decide whether to show loading, error, or render with cached data
  const hasAnyData = !!fetched || !!item || !!itemRef;
  const hasUsableButtons = Array.isArray(effective.buttons) && effective.buttons.length > 0;
  
  const shouldShowLoadingPlaceholder =
    !!id && !Array.isArray(effective.buttons) && !!isLoading && !hasAnyData;

  // Determine active columns according to the ACTUAL rendered container width (not full window)
  const DEFAULT_BPR = [1, 2, 4, 5, 6];
  
  // Memoize bpr array so React can properly detect changes
  const bpr = useMemo(() => {
    if (!effective?.buttons_per_row) {
      return DEFAULT_BPR;
    }
    return [
      effective.buttons_per_row.extra_small ?? DEFAULT_BPR[0],
      effective.buttons_per_row.small ?? DEFAULT_BPR[1],
      effective.buttons_per_row.medium ?? DEFAULT_BPR[2],
      effective.buttons_per_row.large ?? DEFAULT_BPR[3],
      effective.buttons_per_row.extra_large ?? DEFAULT_BPR[4],
    ];
  }, [
    effective?.buttons_per_row?.extra_small,
    effective?.buttons_per_row?.small,
    effective?.buttons_per_row?.medium,
    effective?.buttons_per_row?.large,
    effective?.buttons_per_row?.extra_large,
  ]);
  
  // Use a callback ref so we can reliably attach a ResizeObserver even when we initially render
  // a placeholder (where the grid node doesn't exist yet).
  const [gridNode, setGridNode] = useState(null);
  const gridRef = useCallback((node) => {
    setGridNode(node || null);
  }, []);
  
  // Memoize the column calculator function with current bpr
  const computeColsFromWidth = useMemo(() => {
    return (w) => {
      if (!w || isNaN(w)) return bpr[0];
      if (w >= 1280) return bpr[4];
      if (w >= 1024) return bpr[3];
      if (w >= 768) return bpr[2];
      if (w >= 640) return bpr[1];
      return bpr[0];
    };
  }, [bpr]);
  
  const [measuredWidth, setMeasuredWidth] = useState(null);
  const effectiveWidth = measuredWidth ?? providedContainerWidth ?? previewWidth;
  
  // Calculate current cols based on width and bpr
  const currentCols = useMemo(() => {
    return computeColsFromWidth(effectiveWidth || 1024);
  }, [computeColsFromWidth, effectiveWidth]);
  
  // Keep cols in state for animation/transition purposes
  const [cols, setCols] = useState(currentCols);
  
  // Update cols when currentCols changes (triggered by width or bpr changes)
  useEffect(() => {
    if (currentCols !== cols) {
      setCols(currentCols);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[ButtonCollection] Columns updated:', {
          id,
          effectiveWidth,
          previousCols: cols,
          newCols: currentCols,
          bpr
        });
      }
    }
  }, [currentCols, cols, id, effectiveWidth, bpr]);
  
  // Always measure: we want the true available width, even when parents pass a width hint or when
  // PreviewContext is present (those can represent page/device width, not this container).
  useEffect(() => {
    const el = gridNode;
    if (!el) return;
    
    const update = () => {
      const width = el.clientWidth;
      setMeasuredWidth((prev) => (prev === width ? prev : width));
    };
    
    update();
    let ro;
    try {
      ro = new ResizeObserver(() => update());
      ro.observe(el);
    } catch (_) {
      // Fallback: window resize when ResizeObserver is unavailable
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    return () => {
      try { ro && ro.disconnect(); } catch (_) {}
    };
  }, [gridNode]);

  const { base: gridContainerStyle } = computeColumnsStyle(effective, cols, preview);
  const buttons = useMemo(() => (Array.isArray(effective.buttons) ? effective.buttons.filter(Boolean) : []).slice().sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0)), [effective.buttons]);
  
  // Create signature for incoming prop/query buttons to track changes
  const buttonsSignature = useMemo(() => buttons.map(b => b.id).join('|'), [buttons]);
  
  const [localButtons, setLocalButtons] = useState(buttons);
  
  // Track the signature of the last intentional local reorder
  const lastReorderSignatureRef = useRef(buttonsSignature);
  
  useEffect(() => {
    // Sync local state when external data changes, but respect ongoing drag suppression
    const localSignature = localButtons.map(b => b.id).join('|');
    
    // If signatures match, no need to update state (prevents render loops)
    if (buttonsSignature === localSignature) return;

    // Check if we are in a suppressed state (recently dragged)
    const isSuppressed = Date.now() < suppressExternalSyncUntilRef.current;
    
    if (isSuppressed) {
      // If the incoming data matches our last intentional reorder, we can accept it early!
      // This allows the UI to confirm the save without waiting for timeout.
      if (buttonsSignature === lastReorderSignatureRef.current) {
        setLocalButtons(buttons);
        // Clear suppression since we are now in sync
        suppressExternalSyncUntilRef.current = 0; 
      }
      return;
    }

    setLocalButtons(buttons);
  }, [buttons, buttonsSignature]); // Depend on signature to avoid object ref churn if possible, though 'buttons' is memoized

  // Keep SortableContext `items` stable when button IDs/order are unchanged.
  // This avoids dnd-kit churn that can cause "Maximum update depth exceeded" loops.
  const itemsIdsSignature = (localButtons || []).map((b) => b?.id).join("|");
  const itemsIds = useMemo(
    () => (localButtons || []).map((b) => makeSortableId({ type: "button", id: b.id })),
    [itemsIdsSignature],
  );

  // Sensor: require the pointer to move at least 8px before drag activates.
  // This prevents accidental drags when the user taps a button to click it.
  // MUST be declared before any early return to satisfy React hooks rules.
  const dragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );
  
  // Direct button creation handler
  const handleAddButton = async (order) => {
    
    if (!effective?.id) {
      console.error('ButtonCollection has no ID');
      return;
    }
    
    try {
      // Step 1: Fetch the button collection to get its component
      const collectionResponse = await apiFetch(`/api/items/button_collection/${effective.id}/`);
      
      if (!collectionResponse.ok) {
        throw new Error('Failed to fetch button collection');
      }
      
      const collection = await collectionResponse.json();
      let componentId = collection.component;
      
      // If button collection is inside a hero, we need to get the hero's component
      if (!componentId && collection.hero) {
        
        const heroResponse = await apiFetch(`/api/items/hero/${collection.hero}/`);
        if (!heroResponse.ok) {
          throw new Error('Failed to fetch hero information');
        }
        
        const hero = await heroResponse.json();
        componentId = hero.component;
      }
      
      if (!componentId) {
        throw new Error('ButtonCollection has no component (and hero has no component either)');
      }
      
      // Step 2: Create a button under the component
      const createButtonResponse = await apiFetch(`/api/components/${componentId}/items/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'button',
          order: 0, // Temporarily add at end of component items
        }),
      });
      
      if (!createButtonResponse.ok) {
        const errorData = await createButtonResponse.json();
        throw new Error(errorData.error || 'Failed to create button');
      }
      
      const newButton = await createButtonResponse.json();
      
      // Step 3: Add the button to the collection at the specified order
      const addToCollectionResponse = await apiFetch(`/api/items/button_collection/${effective.id}/add-button/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          button_id: newButton.id,
          order: order
        }),
      });
      
      if (!addToCollectionResponse.ok) {
        const errorData = await addToCollectionResponse.json();
        throw new Error(errorData.error || 'Failed to add button to collection');
      }
      
      await addToCollectionResponse.json();
      
      // Invalidate and refetch the button collection data
      await queryClient.invalidateQueries({
        queryKey: ["item", "button_collection", effective.id]
      });
      
      // Also refresh the element in edit context if available
      if (editContext?.refreshElement) {
        editContext.refreshElement();
      }
      
    } catch (err) {
      console.error('Failed to add button:', err);
      alert(`Failed to add button: ${err.message}`);
    }
  };

  // After hooks are registered, handle loading/error/null states
  if (!item && !itemRef) return null;
  
  // If we have usable data, render it even if there's an error (network errors should use cached data)
  // Only show error if we have no data at all and there's an error (after all retries)
  const shouldShowError = !hasAnyData && isError && !isLoading;
  
  // If we don't have the collection's buttons yet, show a lightweight placeholder until fetch completes.
  if (shouldShowLoadingPlaceholder) {
    return <div className="h-10 w-full" />;
  }
  
  // Show loading skeleton only if we don't have any data yet and we're still loading
  if (!item && needsFetch && isLoading && !fetched && !hasAnyData) {
    return (
      <div className={`${combinedClasses} ${styleClasses}`} style={inlineStyles}>
        <div className="flex gap-2">
          <div className="h-9 w-24 bg-gray-200 rounded animate-pulse" />
          <div className="h-9 w-24 bg-gray-200 rounded animate-pulse" />
          <div className="h-9 w-24 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    );
  }
  
  // Fallback skeleton if we have only a ref and no fetched data yet (but no error)
  if (!item && needsFetch && !fetched && !error && !hasAnyData) {
    return (
      <div className={`${combinedClasses} ${styleClasses}`} style={inlineStyles}>
        <div className="flex gap-2">
          <div className="h-9 w-24 bg-gray-200 rounded" />
          <div className="h-9 w-24 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }
  
  // Only show error if we have no data at all and there's a persistent error (after retries)
  // Network errors should be handled gracefully by using cached/previous data via placeholderData
  if (shouldShowError) {
    // Distinguish between network errors and other errors for better error messages
    const isNetworkError = error?.isNetworkError || (error?.message?.toLowerCase().includes('network') || error?.message?.toLowerCase().includes('connection'));
    
    // Show error only if we truly have no data at all
    // Note: If hasAnyData is true, shouldShowError would be false, so we only reach here if we have no data
    return (
      <div className={`${combinedClasses} ${styleClasses}`} style={inlineStyles}>
        <div className="text-red-600 text-sm p-2">
          {isNetworkError 
            ? 'Connection lost. Unable to load buttons.' 
            : 'Failed to load buttons'}
        </div>
      </div>
    );
  }

  // Compute grid positioning for each button
  // This handles both alignment and proper wrapping to new rows. Resolve the
  // responsive ``alignment`` wrapper to the breakpoint-specific string that
  // matches the current viewport so the per-button grid placement and the
  // container-level grid (computed in ``computeColumnsStyle``) stay in sync.
  const computeGridStyle = (idx) => {
    const total = localButtons.length;
    const align = (
      resolveButtonCollectionAlignment(effective.alignment, preview) || "center"
    ).toLowerCase();
    
    // Calculate which row this button is in (1-indexed for CSS grid)
    const row = Math.floor(idx / cols) + 1;
    const positionInRow = idx % cols;
    
    // Calculate if this is the last row and if it's incomplete
    const totalRows = Math.ceil(total / cols);
    const isLastRow = row === totalRows;
    const lastRowCount = total % cols === 0 ? cols : total % cols;
    const isIncompleteRow = isLastRow && lastRowCount < cols;
    
    const style = { gridRow: row };
    
    // For 'full' alignment in incomplete last row: make buttons span multiple columns
    if (align === "full" && isIncompleteRow && cols > 1) {
      const span = Math.max(1, Math.floor(cols / lastRowCount));
      style.gridColumn = `span ${span}`;
      return style;
    }
    
    // For other alignments in incomplete last row: position buttons with offset
    if (isIncompleteRow && lastRowCount < cols && cols > 1) {
      let offset = 0;
      if (align === "end") {
        offset = cols - lastRowCount;
      } else if (align === "center") {
        offset = Math.floor((cols - lastRowCount) / 2);
      }
      // Set explicit column position: start at (offset + positionInRow + 1)
      const startCol = offset + positionInRow + 1;
      style.gridColumn = `${startCol} / span 1`;
      return style;
    }
    
    // For complete rows or 'start' alignment: let grid auto-place
    return style;
  };
  const wrapperClasses = `${combinedClasses} ${styleClasses}`.trim();

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    // Suppress the immediate click that browsers can fire after drag release.
    suppressClickUntilRef.current = Date.now() + 300;
    
    const fromIndex = localButtons.findIndex((b) => makeSortableId({ type: "button", id: b.id }) === String(active.id));
    const toIndex = localButtons.findIndex((b) => makeSortableId({ type: "button", id: b.id }) === String(over.id));
    
    if (fromIndex === -1 || toIndex === -1) {
      return;
    }

    const nextWithOrder = reorderArray(localButtons, fromIndex, toIndex);
    
    // Calculate new signature for robust tracking
    const newSignature = nextWithOrder.map(b => b.id).join('|');
    lastReorderSignatureRef.current = newSignature;
    
    // Set suppression window to prevent stale updates from reverting UI
    suppressExternalSyncUntilRef.current = Date.now() + 4000;
    setLocalButtons(nextWithOrder);
    
    try {
      // Optimistically align collection cache to the new order immediately.
      queryClient.setQueryData(["item", "button_collection", effective.id], (prev) => {
        if (!prev || !Array.isArray(prev.buttons)) return prev;
        return {
          ...prev,
          buttons: nextWithOrder,
        };
      });

      // Persist all orders within the collection for stability
      await reorderButtonCollection({
        collectionId: effective.id,
        buttonIds: nextWithOrder.map((b) => b.id),
      });
      
      // Refetch authoritative state from backend and force local state to it.
      // This prevents any stale parent-prop/query snapshots from snapping order back.
      const latestResponse = await apiFetch(`/api/items/button_collection/${effective.id}/`);
      
      if (latestResponse.ok) {
        const latestCollection = await latestResponse.json();
        
        const latestButtons = Array.isArray(latestCollection?.buttons)
          ? latestCollection.buttons.slice().sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
          : [];
          
        const latestSignature = latestButtons.map(b => b.id).join('|');
        
        if (latestSignature === newSignature) {
           // We are in sync, clear suppression so normal updates resume
           suppressExternalSyncUntilRef.current = 0;
           // Update cache authoritatively
           queryClient.setQueryData(["item", "button_collection", effective.id], latestCollection);
           // Update local state just in case (though it should match)
           setLocalButtons(latestButtons);
        } else {
           // If mismatch, we trust the backend but trigger a fresh refetch to catch up eventually
           queryClient.setQueryData(["item", "button_collection", effective.id], latestCollection);
           setLocalButtons(latestButtons);
           
           // Update last known signature so effect doesn't fight it (temporarily accepts the revert)
           lastReorderSignatureRef.current = latestSignature;
           
           // Invalidate to force another fetch (hope for eventual consistency)
           queryClient.invalidateQueries({ queryKey: ["item", "button_collection", effective.id] });
        }
      } else {
        queryClient.invalidateQueries({ queryKey: ["item", "button_collection", effective.id] });
      }
    } catch (err) {
      console.error('Reorder failed:', err);
      // Revert on error? Or keep optimistic state and show error?
      // Usually better to revert if backend failed.
      alert("Failed to reorder buttons. Please refresh.");
      setLocalButtons(buttons); // Revert to original prop state
    }
  };

  const content = (
    <div className={`${wrapperClasses} button-collection-container`} style={inlineStyles}>
      <div
        ref={gridRef}
        className={`w-full relative`}
        style={gridContainerStyle}
        onClickCapture={(e) => {
          if (Date.now() < suppressClickUntilRef.current) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
          <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={itemsIds} strategy={rectSortingStrategy}>
          {localButtons.map((btn, i) => {
          // Get the computed grid style for this button (includes gridRow and gridColumn)
          const buttonGridStyle = computeGridStyle(i);
          
          // Determine if this is single column layout
          const isSingleColumn = cols === 1;
          
          return (
            <SortableWrapper
              key={makeSortableId({ type: "button", id: btn.id })}
              id={makeSortableId({ type: "button", id: btn.id })}
              data={{ parentType: "button_collection", parentId: effective.id, kind: "button", index: i }}
              gridStyle={buttonGridStyle}
            >
              <div className="min-w-0 relative group">
                <Item {...(hasFullData(btn) ? { item: btn } : { itemRef: { id: btn.id, type: "button" } })} />
                
                {/* Add button before first button (shown on hover in edit mode) */}
                {isEditMode && i === 0 && (
                  <AddButtonOverlay
                    layer="button-collection-before"
                    overlayId={`button-collection-before-${effective.id}-${btn.id}`}
                    orientation={isSingleColumn ? "horizontal" : "vertical"}
                    className="absolute transition-opacity opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
                  >
                    <AddButtonWithIndicator
                      small
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddButton(0);
                      }}
                    />
                  </AddButtonOverlay>
                )}
                
                {/* Add button after this button (shown on hover in edit mode) */}
                {isEditMode && (
                  <AddButtonOverlay
                    layer="button-collection-after"
                    overlayId={`button-collection-after-${effective.id}-${btn.id}-${i}`}
                    orientation={isSingleColumn ? "horizontal" : "vertical"}
                    className="absolute transition-opacity opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
                  >
                    <AddButtonWithIndicator
                      small
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddButton(i + 1);
                      }}
                    />
                  </AddButtonOverlay>
                )}
              </div>
            </SortableWrapper>
          );
        })}
          </SortableContext>
        </DndContext>
        
        {/* Add button for empty collection */}
        {isEditMode && localButtons.length === 0 && (
          <AddButtonOverlay
            layer="button-collection-empty"
            overlayId={`button-collection-empty-${effective.id}`}
            strategy="inline"
            className="flex items-center justify-center py-8"
          >
            <AddButtonWithIndicator
              onClick={(e) => {
                e.stopPropagation();
                handleAddButton(0);
              }}
            />
          </AddButtonOverlay>
        )}
      </div>
    </div>
  );

  return <EditableElement element={effective}>{content}</EditableElement>;
};

export default ButtonCollection;


